import {
  DEFAULT_PAGE_SIZE,
  TradeFairSort,
  listTradeFairs,
  type TradeFairPage,
  type TradeFairState,
} from '@/api/tradeFairs'
import type { SortDirection } from '@/api/sorting'
import { usePagedList, type PagedList } from '@/hooks/usePagedList'

export type TradeFairList = PagedList<TradeFairPage, TradeFairSort> & {
  /** Null is "all of them" — the filter is off, not set to a fourth state. */
  state: TradeFairState | null
  setState: (state: TradeFairState | null) => void
}

const FILTERS = ['state'] as const

/** How each column is read, mirroring `TradeFairSort` in the backend. */
const DIRECTIONS: Readonly<Record<TradeFairSort, SortDirection>> = {
  // Newest first: the fair somebody is looking for is almost always a recent one.
  [TradeFairSort.START_DATE]: 'DESC',
  [TradeFairSort.NAME]: 'ASC',
  [TradeFairSort.CITY]: 'ASC',
}

export function useTradeFairList(): TradeFairList {
  const list = usePagedList<TradeFairPage, TradeFairSort>({
    filterKeys: FILTERS,
    directions: DIRECTIONS,
    defaultSort: TradeFairSort.START_DATE,
    initialPageSize: DEFAULT_PAGE_SIZE,
    rowCount: (page) => page.tradeFairs.length,
    load: ({ search, page, size, sort, direction, filters }, signal) =>
      listTradeFairs(
        { search, state: (filters.state || null) as TradeFairState | null, sort, direction, page, size },
        signal,
      ),
  })

  return {
    ...list,
    state: (list.filters.state || null) as TradeFairState | null,
    setState: (state) => list.setFilter('state', state ?? ''),
  }
}
